import React, { useEffect, useState } from 'react';
import { APP } from '../tokens';
import CoTStepIndicator, { type StepStatus } from './CoTStepIndicator';
import CoTCategoryIcon from './CoTCategoryIcon';
import type { CoTStepData } from './types';

const F = APP.fontFamily;

// ─── Inject fade-in keyframe (once) ──────────────────
const FADE_ID = 'cot-timeline-fade-kf';
if (typeof document !== 'undefined' && !document.getElementById(FADE_ID)) {
  const style = document.createElement('style');
  style.id = FADE_ID;
  style.textContent = `@keyframes cotTimelineFadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`;
  document.head.appendChild(style);
}

// ─── Colors ──────────────────────────────────────────

const TITLE_COLOR: Record<StepStatus, string> = {
  done: '#1A1714',
  active: '#2D6E5A',
  pending: '#9E9189',
  error: '#D94545',
  skipped: '#8B7D6B',
  blocked: '#C5BFB8',
  confirm: '#F59E0B',
  clarify: '#6366F1',
};

const TITLE_WEIGHT: Record<StepStatus, number> = {
  done: 500,
  active: 600,
  pending: 400,
  error: 600,
  skipped: 400,
  blocked: 400,
  confirm: 600,
  clarify: 600,
};

const LINE_COLOR_DONE = '#C5D9CE';
const LINE_COLOR_DEFAULT = '#E8E4DD';

// ─── Elapsed time helper ─────────────────────────────

function ElapsedTime({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - startedAt) / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  if (elapsed < 1) return null;
  return (
    <span style={{ fontSize: 10, color: APP.textMuted, fontFamily: F, marginLeft: 8 }}>
      {elapsed}s
    </span>
  );
}

// ─── Single timeline row ─────────────────────────────

interface TimelineRowProps {
  step: CoTStepData;
  isLast: boolean;
  compact: boolean;
  children?: React.ReactNode;
}

function TimelineRow({ step, isLast, compact, children }: TimelineRowProps) {
  const hasChildren = React.Children.count(children) > 0;
  const lineColor = step.status === 'done' || step.status === 'skipped'
    ? LINE_COLOR_DONE
    : LINE_COLOR_DEFAULT;

  return (
    <div
      data-cot-edit-batch-id={step.editBatch?.id}
      style={{
        display: 'flex',
        gap: 10,
        animation: 'cotTimelineFadeIn 200ms ease forwards',
      }}
    >
      {/* Icon + connector column */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 14,
        }}
      >
        {/* Step indicator */}
        <div style={{ flexShrink: 0, paddingTop: compact ? 1 : 2 }}>
          <CoTStepIndicator status={step.status} size={compact ? 12 : 14} />
        </div>

        {/* Vertical connector line */}
        {!isLast && (
          <div
            style={{
              width: 1,
              flexGrow: 1,
              minHeight: compact ? 8 : 12,
              backgroundColor: lineColor,
              transition: 'background-color 300ms ease',
            }}
          />
        )}
      </div>

      {/* Content column */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minWidth: 0,
          paddingBottom: isLast ? 0 : compact ? 4 : 8,
          gap: hasChildren ? 4 : 2,
        }}
      >
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {step.category && (
            <CoTCategoryIcon
              category={step.category}
              size={compact ? 10 : 12}
              active={step.status === 'active'}
            />
          )}
          <span
            style={{
              fontSize: compact ? 11 : 12,
              fontWeight: compact ? 500 : TITLE_WEIGHT[step.status],
              color: compact ? '#6B6560' : TITLE_COLOR[step.status],
              lineHeight: '16px',
              fontFamily: F,
            }}
          >
            {step.title}
          </span>
          {step.status === 'active' && step.startedAt && (
            <ElapsedTime startedAt={step.startedAt} />
          )}
        </div>

        {/* Description (not in compact mode) */}
        {!compact && step.description && (step.status === 'done' || step.status === 'active' || step.status === 'confirm' || step.status === 'clarify') && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: APP.textMuted,
              lineHeight: '15px',
              fontFamily: F,
            }}
          >
            {step.description}
          </span>
        )}

        {/* Children (error cards, edit review, confirm cards, sources, etc.) */}
        {(!compact || !!step.editBatch) && children}
      </div>
    </div>
  );
}

// ─── Planning skeleton step ──────────────────────────

function PlanningStep() {
  return (
    <TimelineRow
      step={{ title: 'Analyzing…', status: 'active' }}
      isLast
      compact={false}
    />
  );
}

// ─── Main component ──────────────────────────────────

interface AgentProgressTimelineProps {
  steps: CoTStepData[];
  status: 'idle' | 'planning' | 'working' | 'done' | 'error';
  /** When true, render compact single-line rows (used when all steps done). */
  compact?: boolean;
  /** Render children for each step. Receives (step, stepIndex). */
  renderStepChildren?: (step: CoTStepData, index: number) => React.ReactNode;
}

export default function AgentProgressTimeline({
  steps,
  status,
  compact = false,
  renderStepChildren,
}: AgentProgressTimelineProps) {
  // Planning state with no steps yet: show skeleton
  if (status === 'planning' && steps.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', paddingBlock: 4 }}>
        <PlanningStep />
      </div>
    );
  }

  if (steps.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingBlock: 4 }}>
      {steps.map((step, i) => (
        <TimelineRow
          key={i}
          step={step}
          isLast={i === steps.length - 1}
          compact={compact}
        >
          {renderStepChildren?.(step, i)}
        </TimelineRow>
      ))}
    </div>
  );
}
